﻿import { useState } from 'react';
import { StrikeThroughHeader } from '../../../../components/Headers/strikethrough-header/strikethrough-header';
import { GetISDCategoryList, GetISDMarketingList, EmailForm } from '../../../../api/admin';
import { useQuery } from '@tanstack/react-query';
import ISDMarketingEmailModule from '../ISDMarketing/ISDMarketingEmailModule';
import { Card } from 'Components/Cards/card/card';
import LoadingIndicator from 'Components/Global/loading-indicator/loading-indicator';
import { XMasonry, XBlock } from 'react-xmasonry';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import NotificationSnackbar from '../../../../components/Global/NotificationSnackbar/NotificationSnackbar';
import { fetchSignedURL } from '../../../../api/file';
import { useLinkTypes } from '../../../../hooks/useLinkTypes';
import CircularProgress from '@mui/material/CircularProgress';

export default function ISDMarketing() {
  const query = useQuery({
    queryKey: ['GetISDCategoryList'],
    queryFn: async () => await GetISDCategoryList()(),
  });

  if (query.isPending) {
    return (
      <>
        <StrikeThroughHeader>Independent Sales Marketing</StrikeThroughHeader>
        <LoadingIndicator />
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        <StrikeThroughHeader>Independent Sales Marketing</StrikeThroughHeader>
        <div>Something went wrong.</div>
      </>
    );
  }
  if (query.isSuccess) {
    return <ISDMarketingListView data={query.data} />;
  }
}

function ISDMarketingListView({ data }) {
  const lstQuery = useQuery({
    queryKey: ['GetISDMarketingList'],
    queryFn: async () => await GetISDMarketingList()(),
  });

  const [hideIcons, setHideIcons] = useState(false);
  const [attachedForms, setAttachedForms] = useState([]);
  const [attachedLinks, setAttachedLinks] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [status, setStatus] = useState('');
  const [showNotif, setShowNotif] = useState(false);
  const linkTypeQuery = useLinkTypes();

  function HideEmailModuleCallback() {
    setStatus('');
    setShowEmailModal(false);
    setAttachedForms([]);
    setAttachedLinks([]);
    setHideIcons(false);
    setShowNotif(false);
  }

  const handleSecuredFile = async (path) => {
    await fetchSignedURL(path);
  };

  function HandleAddForm(form) {
    const forms = attachedForms;
    setAttachedForms([...forms, form]);
  }

  function HandleRemoveForm(form) {
    const forms = attachedForms;
    if (forms.length > 0) {
      const result = forms.filter((f) => f.id !== form.id);

      setAttachedForms(result);
    }
  }

  function HandleFileAttachment(value, form) {
    if (value) {
      HandleAddForm(form);
    } else {
      HandleRemoveForm(form);
    }
  }

  function CheckIfFormAttached(form) {
    return attachedForms.indexOf(form) > -1;
  }

  function HandleAddLink(link) {
    const links = attachedLinks;
    setAttachedLinks([...links, link]);
  }

  function HandleRemoveLink(link) {
    const links = attachedLinks;
    if (links.length > 0) {
      const result = links.filter((l) => l.id !== link.id);

      setAttachedLinks(result);
    }
  }

  function HandleLinkAttachment(value, link) {
    if (value) {
      HandleAddLink(link);
    } else {
      HandleRemoveLink(link);
    }
  }

  function CheckIfLinkAttached(link) {
    return attachedLinks.indexOf(link) > -1;
  }

  return (
    <>
      <StrikeThroughHeader>Independent Sales Marketing</StrikeThroughHeader>

      {linkTypeQuery.isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <NotificationSnackbar
            status={status}
            openTrigger={showNotif}
            successMsg="Email successfully sent"
            errorMsg={'There was an error sending the email'}
          />

          {(attachedForms.length > 0 || attachedLinks.length > 0) && (
            <div style={{ textAlign: 'right' }}>
              <Tooltip title="Email with Attached Forms" placement="top" arrow>
                <Button
                  style={{ color: 'white' }}
                  size="small"
                  variant="contained"
                  id="btnStartOver"
                  name="StartOver"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowEmailModal(true);
                    setShowNotif(false);
                    setStatus('');
                  }}
                >
                  Send Email
                </Button>
              </Tooltip>
            </div>
          )}

          <XMasonry maxColumns={3} responsive targetBlockWidth={500}>
            {data !== null &&
              data.map((cat) => (
                <XBlock key={cat.name + cat.id}>
                  <div className="m-2">
                    <Card
                      title={cat.name ? cat.name : 'Unnamed ISD Marketing Group'}
                      height="auto"
                      headingFontColor="white"
                      headingHeight="3em"
                      width="100%"
                    >
                      <TableContainer style={{ overflow: 'hidden' }}>
                        <Table stickyHeader>
                          <TableBody>
                            {lstQuery.data != null &&
                              lstQuery.data.map((form) => {
                                let returnElement;

                                if (form.isdMarketingCategoryID == cat.id) {
                                  returnElement = (
                                    <TableRow key={form.id}>
                                      <TableCell>
                                        <Tooltip title="Attach to Email" placement="top" arrow>
                                          <Checkbox
                                            disabled={
                                              form.linkTypeID == linkTypeQuery.LINKTYPE.NOTHING
                                            }
                                            checked={
                                              CheckIfFormAttached(form) || CheckIfLinkAttached(form)
                                            }
                                            onClick={(e) => {
                                              if (form.linkTypeID == linkTypeQuery.LINKTYPE.FILE) {
                                                HandleFileAttachment(e.target.checked, form);
                                              } else {
                                                HandleLinkAttachment(e.target.checked, form);
                                              }
                                              setHideIcons(
                                                e.target.checked == true ||
                                                  attachedForms.length > 1 ||
                                                  attachedLinks.length > 1
                                              );
                                            }}
                                            className="select-item"
                                          />
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell>
                                        <label>{form.title}</label>
                                      </TableCell>
                                      {!hideIcons && (
                                        <TableCell>
                                          <div style={{ display: 'flex' }}>
                                            <Tooltip
                                              title="Open Link or Preview"
                                              placement="top"
                                              arrow
                                            >
                                              <span className="form-icon-wrapper">
                                                {form.linkTypeID ===
                                                linkTypeQuery.LINKTYPE
                                                  .NOTHING ? null : form.linkTypeID ===
                                                  linkTypeQuery.LINKTYPE.FILE ? (
                                                  <a
                                                    role="button"
                                                    style={{ padding: '0' }}
                                                    href="#"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      fetchSignedURL(
                                                        `${process.env.REACT_APP_AGENTPORTAL_PREFIX}/ISDMarketing\\${form.linkToResource}`
                                                      );
                                                    }}
                                                  >
                                                    <OpenInNewIcon />
                                                  </a>
                                                ) : (
                                                  <a
                                                    role="button"
                                                    style={{ padding: '0' }}
                                                    href={form.linkToResource}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    <OpenInNewIcon />
                                                  </a>
                                                )}
                                              </span>
                                            </Tooltip>

                                            <Tooltip title="Email Form" placement="top" arrow>
                                              <span className="form-icon-wrapper">
                                                <EmailIcon
                                                  onClick={() => {
                                                    if (
                                                      form.linkTypeID ==
                                                      linkTypeQuery.LINKTYPE.NOTHING
                                                    ) {
                                                      return;
                                                    }
                                                    if (
                                                      form.linkTypeID == linkTypeQuery.LINKTYPE.FILE
                                                    ) {
                                                      HandleFileAttachment(true, form);
                                                    } else {
                                                      HandleLinkAttachment(true, form);
                                                    }
                                                    setShowEmailModal(true);
                                                  }}
                                                />
                                              </span>
                                            </Tooltip>
                                          </div>
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  );
                                }

                                return returnElement;
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  </div>
                </XBlock>
              ))}
          </XMasonry>

          <ISDMarketingEmailModule
            attachedForms={attachedForms}
            attachedLinks={attachedLinks}
            showEmailModal={showEmailModal}
            HideEmailModuleCallback={HideEmailModuleCallback}
            setStatus={setStatus}
            setShowNotif={setShowNotif}
          />
        </>
      )}
    </>
  );
}
